/**
 * Rider Detail Page
 * 
 * Displays complete rider information including:
 * - Rider profile details
 * - Participation history for selected season
 * - Associated users with contact info
 * - Time trial results
 * 
 * Requirements: 4.1, 4.8
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useRidersStore from '../store/ridersStore';
import { useAuth } from '../auth/AuthProvider';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { format } from 'date-fns';
import { restClient } from '../api/rest-client';
import type { Incident } from '../types/incidents';
import {
  isRenderableIncident,
  sortIncidents,
  NOT_RECORDED,
} from './admin/IncidentsReport';
import ParticipationHistoryCard from '../components/features/riders/ParticipationHistoryCard';
import { formatPosition, formatTime } from '../utils/raceResults';
import { formatRiderName, getStatusBadgeColor } from '../utils/riderDisplay';
import { formatPhoneParenthesized, isDisplayablePhone } from '../utils/phone';
import { useEventsStore } from '../store/eventsStore';
import RaceRegistrationStatus from '@/components/features/riders/RaceRegistrationStatus';
import { filterRaceEvents } from '@/utils/raceRegistration';

/**
 * Parse associated users from JSON string
 */
/**
 * One entry of a rider's `associatedUsers` array, as returned by
 * `GET /riders?rider_id=`.
 *
 * `phone` is added at read time by `riders-get.py` from Cognito and is ABSENT
 * (never '' or null) when the user has no phone or could not be resolved.
 * `email` is absent on 39% of production associations, which is why
 * `resolveAssociationEmail` falls back to `user_id`.
 *
 * Declared locally rather than reusing `AssociationEntry` from
 * `utils/associations.ts`: this page does not use the `resolveAssociation`
 * helpers yet, and converging the types belongs with the deferred "Former user"
 * work that would introduce them.
 */
type AssociatedUserEntry = {
  user_id: string;
  role: string;
  name?: string;
  email?: string;
  phone?: string;
};

function parseAssociatedUsers(associatedUsersJson?: string): AssociatedUserEntry[] {
  if (!associatedUsersJson) {
    return [];
  }

  try {
    const parsed = JSON.parse(associatedUsersJson);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch (error) {
    return [];
  }
}

/** Shown in place of any contact detail that is missing or unusable. */
const NOT_AVAILABLE = 'N/A';

/**
 * The email address to display for an association, or undefined when there
 * isn't a usable one.
 *
 * Prefers the stored `email`, falling back to `user_id`. The fallback is load
 * bearing, not cosmetic: 86 of 221 production associations (39%) have no
 * `email` field, while all 221 have a `user_id` that is an email address.
 * Without it, two in five associated users would show "N/A" for an address the
 * page displays today.
 *
 * The fallback applies ONLY when the value looks like an email. Associations
 * keyed by a Cognito `sub` (UUID) are possible in principle, and rendering a
 * UUID as a mailto: target would be worse than showing nothing. No such rows
 * exist today, so this is a guard against future data.
 */
export function resolveAssociationEmail(
  entry: Pick<AssociatedUserEntry, 'email' | 'user_id'>
): string | undefined {
  const stored = entry.email?.trim();
  if (stored) return stored;

  const fallback = entry.user_id?.trim();
  if (fallback && fallback.includes('@')) return fallback;

  return undefined;
}

/**
 * Associated users ordered by display name, matching the Dashboard rider card.
 *
 * Returns a NEW array; never mutates the input. `Array.prototype.sort` is
 * stable, so entries with equal names keep their stored relative order and the
 * result is deterministic.
 */
export function sortAssociationsByName<T extends { name?: string }>(entries: T[]): T[] {
  return [...entries].sort((a, b) =>
    (a.name || NOT_AVAILABLE).localeCompare(b.name || NOT_AVAILABLE)
  );
}

/**
 * Contact details for one associated user.
 *
 * Mirrors the Dashboard rider card's Contact Information block (Name /
 * Relationship / Phone / Text / Email) so the two surfaces read the same.
 *
 * Phone and Text intentionally show the same number: there is exactly one phone
 * field in the system, and the rows differ only in link scheme. The hrefs use
 * the RAW E.164 value while the label is formatted, because dialers handle
 * `tel:+17705956403` more reliably than `tel:(770) 595-6403`.
 */
function AssociatedUserContactCard({ entry }: { entry: AssociatedUserEntry }) {
  const phoneIsShown = isDisplayablePhone(entry.phone);
  const phoneLabel = phoneIsShown ? formatPhoneParenthesized(entry.phone) : NOT_AVAILABLE;
  const rawPhone = entry.phone ?? '';
  const email = resolveAssociationEmail(entry);

  return (
    <div
      className="p-3 sm:p-4 border-l-4 border-primary-500 bg-primary-50 rounded"
      data-testid="associated-user-card"
    >
      <p className="mb-2">
        <strong className="text-primary-900">Name:</strong>{' '}
        <span className="text-primary-700">{entry.name || NOT_AVAILABLE}</span>
      </p>
      <p className="mb-2">
        <strong className="text-primary-900">Relationship:</strong>{' '}
        <span className="text-primary-700">{entry.role || NOT_AVAILABLE}</span>
      </p>
      <p className="mb-2">
        <strong className="text-primary-900">Phone:</strong>{' '}
        {phoneIsShown ? (
          <a
            href={`tel:${rawPhone}`}
            className="text-primary-600 hover:text-primary-800 underline"
            data-testid="associated-user-phone-link"
          >
            {phoneLabel}
          </a>
        ) : (
          <span className="text-primary-700">{NOT_AVAILABLE}</span>
        )}
      </p>
      <p className="mb-2">
        <strong className="text-primary-900">Text:</strong>{' '}
        {phoneIsShown ? (
          <a
            href={`sms:${rawPhone}`}
            className="text-primary-600 hover:text-primary-800 underline"
            data-testid="associated-user-text-link"
          >
            {phoneLabel}
          </a>
        ) : (
          <span className="text-primary-700">{NOT_AVAILABLE}</span>
        )}
      </p>
      <p>
        <strong className="text-primary-900">Email:</strong>{' '}
        {email ? (
          <a
            href={`mailto:${email}`}
            className="text-primary-600 hover:text-primary-800 underline"
            data-testid="associated-user-email-link"
          >
            {email}
          </a>
        ) : (
          <span className="text-primary-700">{NOT_AVAILABLE}</span>
        )}
      </p>
    </div>
  );
}

/**
 * Format time trial time (seconds to MM:SS)
 */
function formatTimeTrialTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/** Inline-expand cutoff for the incident description preview. */
const INCIDENT_DESCRIPTION_PREVIEW_LIMIT = 200;

/**
 * Truncated description with inline "Show more" toggle — mirrors the
 * same pattern used by `EventIncidents.tsx` and `IncidentsReport.tsx`.
 * Keeps the RiderDetail section visually consistent with the canonical
 * incident surfaces without coupling the two files at the module
 * boundary (the helper is trivial and already duplicated across both
 * pages).
 */
function IncidentRowDescription({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const safe = typeof text === 'string' ? text : '';
  const needsTruncation = safe.length > INCIDENT_DESCRIPTION_PREVIEW_LIMIT;
  if (!needsTruncation) {
    return (
      <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
        {safe}
      </p>
    );
  }
  return (
    <div className="text-sm text-gray-800">
      <p className="whitespace-pre-wrap break-words">
        {expanded
          ? safe
          : `${safe.slice(0, INCIDENT_DESCRIPTION_PREVIEW_LIMIT)}…`}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1 text-primary-600 hover:text-primary-700 underline text-sm min-h-[44px]"
        aria-expanded={expanded}
      >
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
}

/**
 * Format an incident's event date. Uses the enriched `event_date` when
 * it parses, falls back to the `NOT_RECORDED` sentinel otherwise so a
 * missing/invalid date never blanks the row.
 */
function formatIncidentEventDate(iso: string | undefined | null): string {
  if (!iso) return NOT_RECORDED;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return NOT_RECORDED;
    return format(d, 'MMM d, yyyy');
  } catch {
    return NOT_RECORDED;
  }
}

/**
 * Rider Detail Page Component
 */
export function RiderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Get rider data from store
  const {
    selectedRider,
    selectedRiderStats,
    selectedSeason,
    loading,
    error,
    selectRider,
    fetchRiderStats,
    clearSelectedRider,
  } = useRidersStore();

  // Get auth state for role-based actions
  const { isAdmin, isCoach: isCoachUser, user } = useAuth();

  // Get events for race registration display
  const { events: allEvents, fetchEvents } = useEventsStore();
  
  // Check if current user is associated with this rider
  const isAssociatedUser = useMemo(() => {
    if (!selectedRider || !user) return false;
    
    const associatedUsers = parseAssociatedUsers(selectedRider.associatedUsers);
    return associatedUsers.some(assoc => 
      assoc.user_id === user.email || 
      assoc.user_id === user.username
    );
  }, [selectedRider, user]);
  
  // User can view/edit if they're admin, coach, or associated with the rider
  const canView = isAdmin || isCoachUser || isAssociatedUser;
  const showEditButton = isAdmin || isAssociatedUser; // Coaches can view but not edit

  // Local state for season selection and users
  // Default to most recent season from rider's seasons_active, or current year
  const getDefaultSeason = () => {
    if (selectedRider?.seasons_active && selectedRider.seasons_active.length > 0) {
      // Sort seasons descending and return the most recent
      return [...selectedRider.seasons_active].sort((a, b) => parseInt(b) - parseInt(a))[0];
    }
    return selectedSeason || new Date().getFullYear().toString();
  };
  
  const [statsSeason, setStatsSeason] = useState<string>(getDefaultSeason());
  const [allTimeTrials, setAllTimeTrials] = useState<any[]>([]);
  const [loadingTimeTrials, setLoadingTimeTrials] = useState(false);
  const [timeTrialsSeason, setTimeTrialsSeason] = useState<string>('');
  const [allRaceResults, setAllRaceResults] = useState<any[]>([]);
  const [loadingRaceResults, setLoadingRaceResults] = useState(false);
  const [raceResultsSeason, setRaceResultsSeason] = useState<string>('');
  const [eventsCache, setEventsCache] = useState<Map<string, any>>(new Map());

  // Incidents section state (post-launch enhancement). Only fetched for
  // admins/coaches — the backend returns 403 for everyone else and we
  // don't want the noise in the network tab.
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [incidentsError, setIncidentsError] = useState<string | null>(null);

  // NOTE: a local `getUserName` fallback used to live here. Its only caller was
  // the Associated Users section, which now renders the `name` that
  // `riders-get` resolves from Cognito at read time, so the fallback became
  // dead code and `noUnusedLocals` correctly rejected it. Its last behaviour --
  // returning the raw userId when no name was known -- is also the thing the
  // shared association helpers exist to prevent, so it is not worth preserving.

  // Fetch rider on mount
  useEffect(() => {
    if (id) {
      // Force refresh to get latest data (in case we just edited)
      selectRider(id, true);
    }

    return () => {
      clearSelectedRider();
    };
  }, [id, selectRider, clearSelectedRider]);

  // Update statsSeason when selectedRider changes
  useEffect(() => {
    if (selectedRider?.seasons_active && selectedRider.seasons_active.length > 0) {
      // Sort seasons descending and set to most recent
      const mostRecentSeason = [...selectedRider.seasons_active].sort((a, b) => parseInt(b) - parseInt(a))[0];
      setStatsSeason(mostRecentSeason);
    }
  }, [selectedRider]);

  // Set default season for time trials and race results (current year)
  useEffect(() => {
    if (selectedRider?.seasons_active && selectedRider.seasons_active.length > 0) {
      const currentYear = new Date().getFullYear().toString();
      // Check if current year is in the rider's active seasons
      if (selectedRider.seasons_active.includes(currentYear)) {
        setTimeTrialsSeason(currentYear);
        setRaceResultsSeason(currentYear);
      } else {
        // Fall back to most recent season
        const mostRecentSeason = [...selectedRider.seasons_active].sort((a, b) => parseInt(b) - parseInt(a))[0];
        setTimeTrialsSeason(mostRecentSeason);
        setRaceResultsSeason(mostRecentSeason);
      }
    }
  }, [selectedRider]);

  // Fetch rider stats when rider or season changes
  useEffect(() => {
    if (selectedRider && statsSeason) {
      fetchRiderStats(selectedRider.rider_id, statsSeason);
    }
  }, [selectedRider, statsSeason, fetchRiderStats]);

  // Fetch ALL time trials for this rider (no season filter)
  useEffect(() => {
    async function fetchAllTimeTrials() {
      if (!selectedRider) return;
      
      setLoadingTimeTrials(true);
      try {
        const trials = await restClient.getTimeTrials({
          rider_id: selectedRider.rider_id
          // No season filter - get all time trials
        });
        setAllTimeTrials(trials);
      } catch (error) {
        console.error('Failed to fetch time trials:', error);
        setAllTimeTrials([]);
      } finally {
        setLoadingTimeTrials(false);
      }
    }
    
    fetchAllTimeTrials();
  }, [selectedRider]);

  // Fetch race results for this rider and enrich with event details
  useEffect(() => {
    async function fetchRaceResults() {
      if (!selectedRider) return;
      
      setLoadingRaceResults(true);
      try {
        // Fetch ALL race results (no season filter - we'll filter client-side by event date)
        const params: any = { rider_id: selectedRider.rider_id };
        const results = await restClient.getRaceResults(params);
        
        // Get unique event IDs from results
        const eventIds = [...new Set(results.map(r => r.event_id))];
        
        // Fetch all events if not already cached
        let eventsMap = eventsCache;
        if (eventsMap.size === 0 && eventIds.length > 0) {
          try {
            const allEvents = await restClient.getEvents();
            eventsMap = new Map(allEvents.map(e => [e.event_id, e]));
            setEventsCache(eventsMap);
          } catch (error) {
            console.error('Failed to fetch events:', error);
          }
        }
        
        // Enrich results with event details from cache
        const enrichedResults = results.map((result) => {
          const event = eventsMap.get(result.event_id);
          return {
            ...result,
            event_name: event?.event_name || result.event_id,
            event_date: event?.event_starttime || null,
          };
        });
        
        // Filter by event date year (not season field) if a season is selected
        let filteredResults = enrichedResults;
        if (raceResultsSeason) {
          filteredResults = enrichedResults.filter((result) => {
            if (!result.event_date) return false;
            const eventYear = new Date(result.event_date).getFullYear().toString();
            return eventYear === raceResultsSeason;
          });
        }
        
        setAllRaceResults(filteredResults);
      } catch (error) {
        console.error('Failed to fetch race results:', error);
        setAllRaceResults([]);
      } finally {
        setLoadingRaceResults(false);
      }
    }
    
    fetchRaceResults();
  }, [selectedRider, raceResultsSeason, eventsCache]);

  /**
   * Fetch incidents for this rider (coach/admin only).
   *
   * The backend (`practice-incidents-get.py`) enforces
   * `require_coach_or_admin`, so regular users — including a rider's own
   * associated users — receive HTTP 403. Guard the fetch at the
   * component level so non-privileged users never trigger a wasted
   * request.
   *
   * Requirements: rider detail incident history (post-launch enhancement)
   */
  const loadIncidents = useCallback(async () => {
    if (!selectedRider) return;
    if (!(isAdmin || isCoachUser)) return;
    setLoadingIncidents(true);
    setIncidentsError(null);
    try {
      const fresh = await restClient.getIncidentsByRider(selectedRider.rider_id);
      setIncidents(Array.isArray(fresh) ? fresh : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RiderDetail] Failed to load rider incidents', err);
      const maybe = err as {
        response?: { data?: { error?: unknown; message?: unknown } };
        message?: unknown;
      };
      const serverError = maybe?.response?.data?.error;
      const serverMessage = maybe?.response?.data?.message;
      const msg =
        (typeof serverError === 'string' && serverError.length > 0 && serverError) ||
        (typeof serverMessage === 'string' && serverMessage.length > 0 && serverMessage) ||
        (typeof maybe?.message === 'string' && maybe.message.length > 0 && maybe.message) ||
        'Could not load incidents';
      setIncidentsError(msg);
    } finally {
      setLoadingIncidents(false);
    }
  }, [selectedRider, isAdmin, isCoachUser]);

  useEffect(() => {
    if (!selectedRider) return;
    if (!(isAdmin || isCoachUser)) {
      // Make sure we don't leak stale data from a previous session where
      // this was a privileged user.
      setIncidents([]);
      setIncidentsError(null);
      return;
    }
    loadIncidents();
  }, [selectedRider, isAdmin, isCoachUser, loadIncidents]);

  /**
   * Handle season change for stats
   */
  const handleStatsSeasonChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatsSeason(e.target.value);
  }, []);

  // Fetch events for race registration status display
  useEffect(() => {
    const currentSeason = new Date().getFullYear().toString();
    fetchEvents(currentSeason);
  }, [fetchEvents]);

  // Compute race events for the current season
  const raceEvents = useMemo(() => {
    const currentSeason = new Date().getFullYear().toString();
    const seasonEvents = allEvents.filter(e => e.season === currentSeason);
    return filterRaceEvents(seasonEvents);
  }, [allEvents]);

  // Filtered + sorted incidents for the Incidents section. Apply the
  // same row-suppression predicate as IncidentsReport so broken-
  // identifier rows never render (Property 34), then sort by event
  // date desc, timestamp desc (sortIncidents preserves API order if
  // any date fails to parse).
  const renderableIncidents = useMemo(
    () => incidents.filter(isRenderableIncident),
    [incidents],
  );
  const sortedIncidents = useMemo(
    () => sortIncidents(renderableIncidents),
    [renderableIncidents],
  );

  /**
   * Handle edit rider
   */
  const handleEdit = useCallback(() => {
    if (selectedRider) {
      navigate(`/riders/${selectedRider.rider_id}/edit`);
    }
  }, [selectedRider, navigate]);

  /**
   * Handle back to list
   */
  const handleBack = useCallback(() => {
    window.history.length > 1 ? navigate(-1) : navigate('/riders');
  }, [navigate]);

  if (loading && !selectedRider) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading rider</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <Button variant="primary" onClick={handleBack}>
                Back to Riders
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!selectedRider) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900">Rider not found</h3>
            <div className="mt-6">
              <Button variant="primary" onClick={handleBack}>
                Back to Riders
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Check if user has permission to view this rider
  if (!canView) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">You don't have permission to view this rider</p>
            <div className="mt-6">
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const isCoach = selectedRider.type === 'Coach';
  const statusColor = getStatusBadgeColor(selectedRider.status);
  const associatedUsers = parseAssociatedUsers(selectedRider.associatedUsers);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        {/* Back button - full width on mobile */}
        <div className="mb-4">
          <Button variant="secondary" onClick={handleBack}>
            ← Back
          </Button>
        </div>
        
        {/* Rider info and actions - stack on mobile, side-by-side on desktop */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{formatRiderName(selectedRider)}</h1>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`inline-block px-3 py-1 text-xs font-semibold text-white ${isCoach ? 'bg-blue-500' : 'bg-green-500'} rounded-full`}>
                {isCoach ? 'Coach' : 'Student'}
              </span>
              <span className={`inline-block px-3 py-1 text-xs font-semibold text-white ${statusColor} rounded-full capitalize`}>
                {selectedRider.status}
              </span>
            </div>
          </div>

          {showEditButton && (
            <Button variant="primary" onClick={handleEdit} className="w-full sm:w-auto">
              Edit Rider
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Information */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Information */}
          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Profile Information</h2>
            <div className="space-y-3">
              {selectedRider.birthday && (() => {
                // Parse date components to avoid timezone issues
                const [year, month, day] = selectedRider.birthday.split('-').map(Number);
                const date = new Date(year, month - 1, day); // month is 0-indexed
                return (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Birthday</label>
                    <p className="text-gray-900">{format(date, 'MMMM d, yyyy')}</p>
                  </div>
                );
              })()}

              {selectedRider.grade && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Grade</label>
                  <p className="text-gray-900">{selectedRider.grade}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <p className="text-gray-900">{selectedRider.category}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Group</label>
                <p className="text-gray-900">{selectedRider.group}</p>
              </div>

              {selectedRider.level && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Level</label>
                  <p className="text-gray-900">{selectedRider.level}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Season</label>
                <p className="text-gray-900">{selectedRider.season}</p>
              </div>
            </div>

            {/* Race Registration Status — Requirements: 15.1, 15.2, 15.3, 15.4, 15.5, 15.6 */}
            {!isCoach && raceEvents.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <RaceRegistrationStatus
                  raceRegistrations={selectedRider.race_registrations ?? {}}
                  raceEvents={raceEvents}
                />
              </div>
            )}
          </Card>

          {/* Associated Users — contact information, matching the Dashboard
              rider card. Phone arrives enriched from `riders-get`; if that
              Lambda has not been deployed yet, every phone reads "N/A" and the
              section still renders. */}
          {associatedUsers.length > 0 && (
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Associated Users</h2>
              <div className="space-y-4">
                {sortAssociationsByName(associatedUsers).map((assoc, index) => (
                  <AssociatedUserContactCard key={index} entry={assoc} />
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right Column - Statistics and History */}
        <div className="lg:col-span-2 space-y-6">
          {/* Season Statistics */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Season Statistics</h2>
              {selectedRider?.seasons_active && selectedRider.seasons_active.length > 0 ? (
                <select
                  value={statsSeason}
                  onChange={handleStatsSeasonChange}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {[...selectedRider.seasons_active]
                    .sort((a, b) => parseInt(b) - parseInt(a))
                    .map((season) => (
                      <option key={season} value={season}>
                        {season}
                      </option>
                    ))}
                </select>
              ) : (
                <span className="text-sm text-gray-500">No active seasons</span>
              )}
            </div>

            {loading && !selectedRiderStats ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : selectedRiderStats ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-primary-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-primary-600">Total Events</p>
                  <p className="text-3xl font-bold text-primary-900 mt-1">
                    {selectedRiderStats.event_count}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-600">Total Hours</p>
                  <p className="text-3xl font-bold text-green-900 mt-1">
                    {selectedRiderStats.total_hours.toFixed(1)}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm py-4">No statistics available for this season</p>
            )}
          </Card>

          {/* Time Trials */}
          {!isCoach && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Time Trial Results</h2>
              {selectedRider?.seasons_active && selectedRider.seasons_active.length > 0 && (
                <select
                  value={timeTrialsSeason}
                  onChange={(e) => setTimeTrialsSeason(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Seasons</option>
                  {[...selectedRider.seasons_active]
                    .sort((a, b) => parseInt(b) - parseInt(a))
                    .map((season) => (
                      <option key={season} value={season}>
                        {season}
                      </option>
                    ))}
                </select>
              )}
            </div>
            {loadingTimeTrials ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : (() => {
              // Filter time trials by date year if season is selected
              const filteredTimeTrials = timeTrialsSeason
                ? allTimeTrials.filter((trial) => {
                    const trialYear = new Date(trial.date).getFullYear().toString();
                    return trialYear === timeTrialsSeason;
                  })
                : allTimeTrials;

              // Sort by date (newest first)
              const sortedTimeTrials = [...filteredTimeTrials].sort((a, b) => {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
              });

              return sortedTimeTrials.length > 0 ? (
                <div className="space-y-3">
                  {sortedTimeTrials.map((trial) => (
                    <div
                      key={trial.trial_id}
                      className="border rounded-lg p-3 sm:p-4 bg-white border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        {/* Left side: Trial info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 mb-2">
                            <span className="text-xl flex-shrink-0">⏱️</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-base sm:text-lg font-semibold text-gray-900">
                                {trial.trail_name || 'Time Trial'}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                <span className="px-2 py-1 rounded text-xs font-medium text-white bg-orange-500 whitespace-nowrap capitalize">
                                  {trial.direction || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Date */}
                          <div className="text-xs sm:text-sm text-gray-600 mb-1 ml-8">
                            📅 {format(new Date(trial.date), 'MMM d, yyyy')}
                          </div>
                          
                          {/* Notes (if applicable) */}
                          {trial.notes && (
                            <div className="mt-2 ml-8 p-2 bg-gray-50 border border-gray-200 rounded text-xs sm:text-sm text-gray-700">
                              {trial.notes}
                            </div>
                          )}
                        </div>
                        
                        {/* Right side: Time */}
                        <div className="text-center sm:text-right sm:ml-4 flex-shrink-0">
                          <div className="text-xl sm:text-2xl font-bold text-orange-600">
                            {formatTimeTrialTime(trial.time)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Time
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm py-4">No time trial results found</p>
              );
            })()}
          </Card>
          )}

          {/* Race Results */}
          {!isCoach && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Race Results</h2>
              {selectedRider?.seasons_active && selectedRider.seasons_active.length > 0 && (
                <select
                  value={raceResultsSeason}
                  onChange={(e) => setRaceResultsSeason(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Seasons</option>
                  {[...selectedRider.seasons_active]
                    .sort((a, b) => parseInt(b) - parseInt(a))
                    .map((season) => (
                      <option key={season} value={season}>
                        {season}
                      </option>
                    ))}
                </select>
              )}
            </div>
            {loadingRaceResults ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="md" />
              </div>
            ) : (() => {
              // Sort race results by event date (newest first)
              const sortedRaceResults = [...allRaceResults].sort((a, b) => {
                // Use event_date if available, otherwise fall back to timestamp
                const dateA = a.event_date ? new Date(a.event_date).getTime() : (a.timestamp ? new Date(a.timestamp).getTime() : 0);
                const dateB = b.event_date ? new Date(b.event_date).getTime() : (b.timestamp ? new Date(b.timestamp).getTime() : 0);
                return dateB - dateA; // Newest first
              });

              return sortedRaceResults.length > 0 ? (
                <div className="space-y-3">
                  {sortedRaceResults.map((result) => (
                  <div
                    key={`${result.event_id}-${result.rider_id}`}
                    className="border rounded-lg p-3 sm:p-4 bg-white border-gray-200 hover:shadow-md transition-shadow"
                  >
                    {/* Mobile: Stack vertically, Desktop: Side by side */}
                    <div className="flex flex-col gap-3">
                      {/* Top section: Event name and position */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <span className="text-xl flex-shrink-0">🏁</span>
                          <div className="flex-1 min-w-0">
                            {/* Event name on its own line, wrapping allowed */}
                            <button
                              onClick={() => navigate(`/events/${result.event_id}`)}
                              className="text-base sm:text-lg font-semibold text-blue-600 hover:underline text-left w-full break-words"
                              title={result.event_name || result.event_id}
                            >
                              {result.event_name || result.event_id}
                            </button>
                            {/* Category badge only */}
                            {result.category && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                <span className="px-2 py-1 rounded text-xs font-medium text-white bg-purple-500 whitespace-nowrap">
                                  {result.category}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Position - aligned to top right */}
                        <div className="text-right flex-shrink-0">
                          <div className="text-xl sm:text-2xl font-bold text-red-600">
                            {formatPosition(result.position, result.total_racers, result.dnf)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Position
                          </div>
                        </div>
                      </div>
                      
                      {/* Details section - full width */}
                      <div className="space-y-1">
                        {/* Date */}
                        <div className="text-xs sm:text-sm text-gray-600">
                          📅 {result.event_date ? format(new Date(result.event_date), 'MMM d, yyyy') : 'Date unknown'}
                        </div>
                        
                        {/* Lap Times (if any) */}
                        {(result.lap_time_1 || result.lap_time_2 || result.lap_time_3 || result.lap_time_4) && (
                          <div className="text-xs sm:text-sm text-gray-600">
                            🔄 Lap Times: 
                            {result.lap_time_1 && <span className="ml-1 font-medium">L1: {formatTime(result.lap_time_1)}</span>}
                            {result.lap_time_2 && <span className="ml-2 font-medium">L2: {formatTime(result.lap_time_2)}</span>}
                            {result.lap_time_3 && <span className="ml-2 font-medium">L3: {formatTime(result.lap_time_3)}</span>}
                            {result.lap_time_4 && <span className="ml-2 font-medium">L4: {formatTime(result.lap_time_4)}</span>}
                          </div>
                        )}
                        
                        {/* Total Time or DNF */}
                        {result.dnf ? (
                          <div className="text-xs sm:text-sm text-gray-600">
                            🏁 <span className="font-medium text-red-600">DNF (Did Not Finish)</span>
                          </div>
                        ) : result.total_time ? (
                          <div className="text-xs sm:text-sm text-gray-600">
                            ⏱️ Total Time: <span className="font-medium">{formatTime(result.total_time)}</span>
                          </div>
                        ) : null}
                        
                        {/* Notes (if applicable) */}
                        {result.notes && (
                          <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs sm:text-sm text-gray-700">
                            {result.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm py-4">No race results found</p>
            );
          })()}
          </Card>
          )}

          {/* Incidents — coach/admin only (post-launch enhancement).
              The backend GET /practice-incidents?rider_id=<id> returns 403
              for regular users, so we also guard the fetch at the
              component level to avoid issuing a request we know will
              fail. Associated users (parents / the rider themselves)
              see no section at all. */}
          {(isAdmin || isCoachUser) && (
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Incidents</h2>
              {loadingIncidents && (
                <div
                  className="flex items-center justify-center py-6"
                  role="status"
                  aria-live="polite"
                >
                  <LoadingSpinner size="md" />
                  <span className="ml-3 text-gray-600">Loading incidents…</span>
                </div>
              )}

              {!loadingIncidents && incidentsError && (
                <div className="border border-red-300 bg-red-50 rounded-md p-4">
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="text-sm text-red-800"
                  >
                    <p className="font-semibold">{incidentsError}</p>
                  </div>
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={loadIncidents}
                      className="min-h-[44px] px-4"
                    >
                      Retry
                    </Button>
                  </div>
                </div>
              )}

              {!loadingIncidents &&
                !incidentsError &&
                sortedIncidents.length === 0 && (
                  <p className="text-gray-700">
                    No incidents reported for this rider yet.
                  </p>
                )}

              {!loadingIncidents &&
                !incidentsError &&
                sortedIncidents.length > 0 && (
                  <ul className="space-y-3" aria-label="Incidents list">
                    {sortedIncidents.map((inc) => {
                      const eventDate = formatIncidentEventDate(inc.event_date);
                      const eventName =
                        (typeof inc.event_name === 'string' &&
                          inc.event_name.trim().length > 0 &&
                          inc.event_name) ||
                        NOT_RECORDED;
                      const incidentType =
                        (typeof inc.incident_type === 'string' &&
                          inc.incident_type.trim().length > 0 &&
                          inc.incident_type) ||
                        NOT_RECORDED;
                      const description =
                        typeof inc.description === 'string' ? inc.description : '';
                      // Reported by: prefer display name, fall back to raw UUID.
                      const displayName =
                        typeof inc.recorded_by_display_name === 'string' &&
                        inc.recorded_by_display_name.trim().length > 0
                          ? inc.recorded_by_display_name
                          : null;
                      const rawRecordedBy =
                        typeof inc.recorded_by === 'string' &&
                        inc.recorded_by.trim().length > 0
                          ? inc.recorded_by
                          : null;
                      const reportedBy =
                        displayName ?? rawRecordedBy ?? NOT_RECORDED;
                      return (
                        <li key={inc.incident_id}>
                          <div className="border rounded-lg p-3 sm:p-4 bg-white border-gray-200 hover:shadow-md transition-shadow">
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs uppercase tracking-wide text-gray-500">
                                    {eventDate}
                                  </p>
                                  <p className="text-base font-semibold text-gray-900 break-words">
                                    {eventName}
                                  </p>
                                </div>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 whitespace-nowrap">
                                  {incidentType}
                                </span>
                              </div>

                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                  Description
                                </p>
                                {description.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic">
                                    {NOT_RECORDED}
                                  </p>
                                ) : (
                                  <IncidentRowDescription text={description} />
                                )}
                              </div>

                              <p className="text-xs text-gray-500 break-words">
                                <span className="font-medium">Reported By:</span>{' '}
                                {reportedBy}
                              </p>

                              <div className="pt-1">
                                <Link
                                  to={`/events/${inc.event_id}/incidents`}
                                  className="inline-flex items-center min-h-[44px] text-sm text-primary-600 hover:text-primary-700 underline break-words"
                                >
                                  View event incidents →
                                </Link>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
            </Card>
          )}

          {/* Participation History */}
          <ParticipationHistoryCard riderId={selectedRider.rider_id} />
        </div>
      </div>
    </div>
  );
}

export default RiderDetail;
